// src/components/backoffice/admins/AdminsPage.tsx
// עם תיקון שדה name ו-Token Expiry

import React, { useState, useEffect, useCallback } from 'react';
import { 
  getAdmins, 
  getAdminDetails, 
  saveAdmin, 
  toggleAdminDelete, 
  restoreAdmin,
  Admin, 
  GetAdminsParams, 
  SaveAdminData 
} from '../../../services/backoffice/adminService';

const AdminsPage: React.FC = () => {
  // State for admins list
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string>('');

  // State for modal
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // ✅ State for form - כולל שדה name
  const [formData, setFormData] = useState<SaveAdminData>({
    id: undefined,
    name: '', // ✅ שדה חובה!
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Load admins from server
  const loadAdmins = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params: GetAdminsParams = {
        page: currentPage,
        limit: limit,
        search: searchTerm,
        show_deleted: showDeleted
      };
      
      console.log('🛡️ [DEBUG] Loading admins with params:', params);
      const response = await getAdmins(params);
      console.log('🛡️ [DEBUG] Admins loaded successfully:', response);
      
      setAdmins(response.admins);
      setTotalPages(response.total_pages);
    } catch (error: any) {
      console.error('🛡️ [DEBUG] Error loading admins:', error);
      
      // ✅ טיפול משופר בשגיאות (כולל session expiry)
      if (error.message === 'Session expired - redirecting to login') {
        // handleSessionError כבר טיפל בהפניה, אין צורך לעשות כלום
        return;
      }
      
      setError(`שגיאה בטעינת המנהלים: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, searchTerm, showDeleted]);

  // Load admins when filters change
  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadAdmins();
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      id: undefined,
      name: '', // ✅ כולל name
      email: '',
      password: ''
    });
    setFormErrors({});
  };

  // Handle create new admin
  const handleCreateNew = () => {
    setIsCreating(true);
    resetForm();
    setShowModal(true);
  };

  // Handle edit admin
  const handleEdit = async (adminId: number) => {
    try {
      console.log('🛡️ [DEBUG] Loading admin details for ID:', adminId);
      const adminDetails = await getAdminDetails(adminId);
      console.log('🛡️ [DEBUG] Admin details loaded:', adminDetails);
      
      setIsCreating(false);
      setFormData({
        id: adminDetails.id,
        name: adminDetails.name, // ✅ כולל name
        email: adminDetails.email,
        password: ''
      });
      setFormErrors({});
      setShowModal(true);
    } catch (error: any) {
      console.error('🛡️ [DEBUG] Error loading admin details:', error);
      
      // ✅ טיפול בשגיאות כולל session expiry
      if (error.message === 'Session expired - redirecting to login') {
        return;
      }
      
      setError(`שגיאה בטעינת פרטי המנהל: ${error.message}`);
    }
  };

  // Handle toggle delete
  const handleToggleDelete = async (adminId: number, isDeleted: boolean) => {
    if (isDeleted) {
      if (!window.confirm('האם אתה בטוח שברצונך לשחזר את המנהל?')) {
        return;
      }
    } else {
      if (!window.confirm('האם אתה בטוח שברצונך למחוק את המנהל?')) {
        return;
      }
    }

    try {
      setProcessingIds(prev => new Set(prev).add(adminId));
      setError('');
      
      if (isDeleted) {
        await restoreAdmin(adminId);
      } else {
        await toggleAdminDelete(adminId);
      }
      
      await loadAdmins();
    } catch (error: any) {
      console.error('🛡️ [DEBUG] Error toggling admin delete:', error);
      
      // ✅ טיפול בשגיאות כולל session expiry
      if (error.message === 'Session expired - redirecting to login') {
        return;
      }
      
      setError(`שגיאה בעדכון סטטוס המנהל: ${error.message}`);
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(adminId);
        return newSet;
      });
    }
  };

  // ✅ Validate form - כולל שדה name
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Name validation ✅
    if (!formData.name?.trim()) {
      errors.name = 'שם המנהל נדרש';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'שם המנהל חייב להכיל לפחות 2 תווים';
    }

    // Email validation
    if (!formData.email?.trim()) {
      errors.email = 'אימייל נדרש';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'אימייל לא תקין';
    }

    // Password validation (required for new admin)
    if (isCreating && !formData.password) {
      errors.password = 'סיסמה נדרשת למנהל חדש';
    } else if (formData.password && formData.password.length < 6) {
      errors.password = 'סיסמה חייבת להיות לפחות 6 תווים';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      const adminData: SaveAdminData = {
        id: formData.id,
        name: formData.name, // ✅ שדה חובה!
        email: formData.email,
        ...(formData.password && { password: formData.password })
      };

      console.log('🛡️ [DEBUG] Submitting admin data:', adminData);
      await saveAdmin(adminData);
      console.log('🛡️ [DEBUG] Admin saved successfully');
      
      setShowModal(false);
      await loadAdmins();
      resetForm();
      
      if (isCreating) {
        alert('המנהל נוצר בהצלחה');
      } else {
        alert('המנהל עודכן בהצלחה');
      }
    } catch (error: any) {
      console.error('🛡️ [DEBUG] Error saving admin:', error);
      
      // ✅ טיפול בשגיאות כולל session expiry
      if (error.message === 'Session expired - redirecting to login') {
        return;
      }
      
      setError(`שגיאה בשמירת המנהל: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle close modal
  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">🛡️ ניהול מנהלי מערכת</h1>
        <button
          onClick={handleCreateNew}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          + הוספת מנהל חדש
        </button>
      </div>

      {/* ✅ Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              חיפוש לפי אימייל
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="הזן אימייל..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              הצגת מנהלים מחוקים
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="mr-2"
              />
              הצג מחוקים
            </label>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              פריטים בעמוד
            </label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
          >
            חפש
          </button>
        </form>
      </div>

      {/* Admins Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="text-gray-500">טוען מנהלים...</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    מזהה
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    שם
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    אימייל
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    תאריך יצירה
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    סטטוס
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {admins.map((admin) => (
                  <tr key={admin.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {admin.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {admin.name} {/* ✅ הוספתי עמודת שם */}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {admin.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(admin.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        admin.is_deleted
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {admin.is_deleted ? 'מחוק' : 'פעיל'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(admin.id)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          ערוך
                        </button>
                        <button
                          onClick={() => handleToggleDelete(admin.id, admin.is_deleted || false)}
                          disabled={processingIds.has(admin.id)}
                          className={`${
                            admin.is_deleted
                              ? 'text-green-600 hover:text-green-900'
                              : 'text-red-600 hover:text-red-900'
                          } disabled:opacity-50`}
                        >
                          {processingIds.has(admin.id) 
                            ? 'מעבד...' 
                            : admin.is_deleted 
                              ? 'שחזר' 
                              : 'מחק'
                          }
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                הקודם
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                הבא
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  עמוד {currentPage} מתוך {totalPages}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    הקודם
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    הבא
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {isCreating ? 'הוספת מנהל חדש' : 'עריכת מנהל'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* ✅ שדה שם - חדש! */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    שם המנהל *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="הזן שם מלא"
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    אימייל *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="הזן אימייל"
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    סיסמה {isCreating ? '*' : '(השאר ריק כדי לא לשנות)'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="הזן סיסמה"
                  />
                  {formErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md transition-colors disabled:opacity-50"
                  >
                    {saving ? 'שומר...' : 'שמור'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-md transition-colors"
                  >
                    בטל
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminsPage;